import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, Tab, Tabs, TextField, Typography, IconButton, Tooltip } from '@mui/material';
import { aiApi, asArray, dateText, errorMessage } from './aiDeveloperShared';
import { ActionButton, Feedback, Status } from './aiDeveloperComponents';
import { DataTable, PageShell, Section } from './aiDeveloperAcademicUI';

const TABS = ['Approval Queue', 'Model Grants', 'API Keys', 'Usage', 'Access Management'];
export default function AIDeveloperAdmin() {
  const [tab, setTab] = useState(0);
  const [applications, setApplications] = useState([]);
  const [queue, setQueue] = useState([]);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [modelRequestId, setModelRequestId] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [catalog, setCatalog] = useState([]);
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState([]);
  const [applicationId, setApplicationId] = useState('');
  const [configId, setConfigId] = useState('');
  const [publicModel, setPublicModel] = useState('');
  const [dailyLimit, setDailyLimit] = useState(100);
  const [monthlyLimit, setMonthlyLimit] = useState(100000);
  const [grants, setGrants] = useState([]);
  const [grantsLoading, setGrantsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [reinstateTarget, setReinstateTarget] = useState(null);
  const [reinstateReason, setReinstateReason] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [apps, models, keyRows, usageRows, queueRows, pending] = await Promise.all([
        aiApi.get('admin/applications'), aiApi.get('catalog'), aiApi.get('admin/keys'), aiApi.get('admin/usage'), aiApi.get('admin/queue'), aiApi.get('admin/pending-model-assignments'),
      ]);
      setApplications(asArray(apps)); setCatalog(asArray(models));
      setKeys(asArray(keyRows)); setUsage(asArray(usageRows)); setQueue(asArray(queueRows)); setPendingAssignments(asArray(pending));
    } catch (err) { setError(errorMessage(err)); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const perform = async (action, message) => {
    setBusy(true); setError(''); setSuccess('');
    try { await action(); await refresh(); if (message) setSuccess(message); }
    catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };

  const decide = (app, status) => {
    if (!window.confirm(`${status} model ${app.RequestedDisplayName} for ${app.ApplicantUser}?`)) return;
    perform(() => aiApi.post(`admin/queue/${app.ModelRequestID}/action`, { Decision: status, ReviewNotes: reviewNotes }), `Queue action recorded: ${status.toLowerCase()}`);
  };
  const loadGrants = async (id) => {
    if (!id) { setGrants([]); return; }
    setGrantsLoading(true);
    try { setGrants(asArray(await aiApi.get(`admin/applications/${id}/grants`))); }
    catch (err) { setError(errorMessage(err)); setGrants([]); }
    finally { setGrantsLoading(false); }
  };

  const chooseApplication = (id) => {
    setModelRequestId(id);
    const request = pendingAssignments.find(a => String(a.ModelRequestID) === String(id));
    const appId = request ? String(request.ApplicationID) : '';
    setApplicationId(appId);
    setGrants([]);
    const requested = request?.RequestedAiConfigId;
    setConfigId(requested ? String(requested) : '');
    const model = catalog.find(c => String(c.AiConfigId) === String(requested));
    setPublicModel(model?.ModelName || '');
    if (appId) loadGrants(appId);
  };

  const saveGrant = () => {
    if (!applicationId || !configId || !publicModel.trim()) { setError('Select an application, provider and public model alias'); return; }
    if (!Number.isInteger(Number(dailyLimit)) || Number(dailyLimit) < 1 || !Number.isInteger(Number(monthlyLimit)) || Number(monthlyLimit) < 1) {
      setError('Quotas must be positive whole numbers'); return;
    }
    perform(async () => {
      await aiApi.post(`admin/applications/${applicationId}/grants`, {
        AiConfigId: Number(configId), PublicModel: publicModel.trim(),
        RequestsPerDay: Number(dailyLimit), TokensPerMonth: Number(monthlyLimit),
      });
      setGrants(asArray(await aiApi.get(`admin/applications/${applicationId}/grants`)));
    }, 'Model grant saved');
  };

  const revokeApplication = () => {
    if (!revokeTarget || revokeReason.trim().length < 3) {
      setError('Provide a revocation reason (at least 3 characters)');
      return;
    }
    const id = revokeTarget.ApplicationID;
    perform(() => aiApi.post(`admin/applications/${id}/revoke`, { Reason: revokeReason.trim() }),
      'Application suspended and all its API keys revoked');
    setRevokeTarget(null);
    setRevokeReason('');
  };

  const reinstateApplication = () => {
    if (!reinstateTarget || reinstateReason.trim().length < 3) { setError('Provide a reinstatement reason (at least 3 characters)'); return; }
    const id=reinstateTarget.ApplicationID;
    perform(() => aiApi.post(`admin/applications/${id}/reinstate`, { Reason: reinstateReason.trim() }),
      'Application reinstated. Reassign revoked models and ask the user to generate a new API key.');
    setReinstateTarget(null); setReinstateReason('');
  };

  const revokeKey = (key) => {
    if (!window.confirm(`Revoke key ${key.KeyPrefix} for ${key.ProjectName || "this project"}?`)) return;
    perform(() => aiApi.delete(`admin/keys/${key.KeyID}`), 'API key revoked');
  };

  const revokeGrant = (grant) => {
    if (!window.confirm(`Revoke model access to ${grant.PublicModel}? Existing keys will no longer access this model.`)) return;
    perform(async () => {
      await aiApi.delete(`admin/grants/${grant.GrantID}`);
      if (applicationId) setGrants(asArray(await aiApi.get(`admin/applications/${applicationId}/grants`)));
    }, 'Model grant revoked');
  };

  const selectedApplication = pendingAssignments.find(a => String(a.ModelRequestID) === String(modelRequestId));
  useEffect(() => {
    if (modelRequestId && !pendingAssignments.some(a => String(a.ModelRequestID) === String(modelRequestId))) {
      setModelRequestId(''); setApplicationId(''); setGrants([]); setConfigId(''); setPublicModel('');
    }
  }, [pendingAssignments, modelRequestId]);
  const [refreshingTab, setRefreshingTab] = useState(null);
  const refreshCurrentTab = async (index) => {
    if (refreshingTab !== null || busy) return;
    setRefreshingTab(index);
    setError('');
    try {
      if (index === 0) setQueue(asArray(await aiApi.get('admin/queue')));
      if (index === 1) {
        const [pending, models] = await Promise.all([aiApi.get('admin/pending-model-assignments'), aiApi.get('catalog')]);
        setPendingAssignments(asArray(pending)); setCatalog(asArray(models));
        if (applicationId) await loadGrants(applicationId);
      }
      if (index === 2) setKeys(asArray(await aiApi.get('admin/keys')));
      if (index === 3) setUsage(asArray(await aiApi.get('admin/usage')));
      if (index === 4) setApplications(asArray(await aiApi.get('admin/applications')));
    } catch (err) { setError(errorMessage(err)); }
    finally { setRefreshingTab(null); }
  };
  const pendingCount = queue.filter(a => a.QueueStatus === 'Pending').length;
  return <PageShell title="AI API Administration" subtitle="Review student/lab requests, assign models and audit usage">
    <Feedback error={error} success={success} clearError={() => setError('')} clearSuccess={() => setSuccess('')} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, borderBottom: '1px solid rgba(25,118,210,0.20)' }}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto"
        sx={{ flex: 1, minWidth: 0, '& .MuiTab-root': { textTransform: 'none', fontWeight: 800 }, '& .Mui-selected': { color: '#F07828 !important' }, '& .MuiTabs-indicator': { backgroundColor: '#F07828', height: 3 } }}>
        {TABS.map(label => <Tab key={label} label={label} />)}
      </Tabs>
      <Tooltip title={`Refresh ${TABS[tab]}`}>
        <span>
          <IconButton size="small" aria-label={`Refresh ${TABS[tab]}`} disabled={refreshingTab !== null || busy}
            onClick={() => refreshCurrentTab(tab)}
            sx={{ flexShrink: 0, mr: 1, color: '#F07828', '&:hover': { bgcolor: '#FFF0E3' } }}>
            <RefreshIcon sx={{ fontSize: 19, animation: refreshingTab === tab ? 'aiTabSpin 0.8s linear infinite' : 'none', '@keyframes aiTabSpin': { to: { transform: 'rotate(360deg)' } } }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
    {tab === 0 && <Section title={`AI Developer Approval Queue (${pendingCount} pending)`}>
      <Typography variant="body2" sx={{ mb: 2 }}>Approvals follow the configured role order. Only the current step approver can act. Final approval enables model grants.</Typography>
      <TextField fullWidth multiline minRows={2} label="Review Notes (optional)" value={reviewNotes}
        onChange={e => setReviewNotes(e.target.value)} sx={{ mb: 2 }} />
      <DataTable rows={queue} columns={[
        { key: 'ApplicantUser', label: 'Requestor' },
        { key: 'ApplicantUser', label: 'Applicant', filter: 'search' }, { key: 'ApplicantType', label: 'Type', filter: 'select' },
        { key: 'Purpose', label: 'Purpose' },
         { key: 'RequestedDisplayName', label: 'Requested Model', render: r => r.RequestedDisplayName ? `${r.RequestedDisplayName} — ${r.RequestedModelName || ''}` : 'Not specified' },
         { key: 'CurrentApproverRole', label: 'Current Role', filter: 'select' },
        { key: 'CurrentStepOrder', label: 'Step' },
        { key: 'QueueStatus', label: 'Queue Status', filter: 'select', render: r => <Status value={r.QueueStatus} /> },
        { key: 'actions', label: 'Actions', render: r => r.CanAct ? <Stack direction="row" gap={1}>
          <Button disabled={busy} color="success" onClick={() => decide(r, 'Approved')}>Approve Step</Button>
          <Button disabled={busy} color="error" onClick={() => decide(r, 'Rejected')}>Reject</Button>
        </Stack> : r.QueueStatus === 'Pending' ? 'Awaiting authorized role' : '—' },
      ]} />
    </Section>}
    {tab === 1 && <Section title="Assign Models to Approved Applications">
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
        <TextField select fullWidth label="Pending Model Assignment" value={modelRequestId} onChange={e => chooseApplication(e.target.value)}
          helperText="Only approved requests whose selected model has not been granted">
          {pendingAssignments.map(a => <MenuItem key={a.ModelRequestID} value={String(a.ModelRequestID)}>{a.ApplicantUser} — {a.RequestedDisplayName || a.RequestedModelName || 'Model'}</MenuItem>)}
        </TextField>
        <TextField select fullWidth label="AI Provider Configuration" value={configId} onChange={e => {
          const value = e.target.value; setConfigId(value);
          const chosen = catalog.find(c => String(c.AiConfigId) === value);
          if (chosen) setPublicModel(chosen.ModelName || '');
        }}>
          {catalog.map(c => <MenuItem key={c.AiConfigId} value={String(c.AiConfigId)}>{c.DisplayName} — {c.ModelName}</MenuItem>)}
        </TextField>
      </Stack>
      {selectedApplication && <Typography variant="body2" sx={{ mb: 2 }}>Student requested: <strong>{selectedApplication.RequestedDisplayName ? `${selectedApplication.RequestedDisplayName} — ${selectedApplication.RequestedModelName}` : "Not specified"}</strong></Typography>}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
        <TextField fullWidth label="Public Model Alias" value={publicModel} onChange={e => setPublicModel(e.target.value)} />
        <TextField fullWidth type="number" label="Daily Requests" value={dailyLimit} onChange={e => setDailyLimit(e.target.value)} />
        <TextField fullWidth type="number" label="Monthly Tokens" value={monthlyLimit} onChange={e => setMonthlyLimit(e.target.value)} />
      </Stack>
      <Stack direction="row" gap={2} sx={{ mb: 2 }}>
        <ActionButton disabled={busy || !applicationId || !configId} onClick={saveGrant}>Save Model Grant</ActionButton>

      </Stack>
      <DataTable rows={grants} columns={[
        { key: 'PublicModel', label: 'Public Model', filter: 'search' }, { key: 'DisplayName', label: 'Provider' },
        { key: 'RequestsPerDay', label: 'Requests / Day' }, { key: 'TokensPerMonth', label: 'Tokens / Month' },
         { key: 'IsActive', label: 'Status', filter: 'select', render: r => <Status value={Number(r.IsActive) ? 'Approved' : 'Revoked'} /> },
         { key: 'action', label: 'Revoke', render: r => Number(r.IsActive) ? <Button color="error" disabled={busy} onClick={() => revokeGrant(r)}>Revoke Model</Button> : '—' },
      ]} empty={grantsLoading ? "Loading existing grants…" : "No existing grants for this request"} />
    </Section>}
    {tab === 2 && <Section title="Issued API Keys (metadata only)">
      <Typography variant="body2" sx={{ mb: 2 }}>Full API key secrets are never displayed here.</Typography>
      <DataTable rows={keys} columns={[
        
        { key: 'ApplicantUser', label: 'Requestor' }, { key: 'ApplicantUser', label: 'Requestor', filter: 'search' }, { key: 'KeyPrefix', label: 'Prefix', filter: 'search' }, { key: 'Label', label: 'Label', filter: 'search' },
        { key: 'ExpiresAt', label: 'Expires', render: r => dateText(r.ExpiresAt) },
        { key: 'status', label: 'Status', render: r => <Status value={r.RevokedAt ? 'Revoked' : 'Approved'} /> },
         { key: 'action', label: 'Revoke', render: r => !r.RevokedAt ? <Button color="error" disabled={busy} onClick={() => revokeKey(r)}>Revoke Key</Button> : '—' },
      ]} />
    </Section>}
    {tab === 3 && <Section title="AI API Usage">
      <DataTable rows={usage} columns={[
        { key: 'ApplicantUser', label: 'Requestor' }, { key: 'ApplicantUser', label: 'Requestor', filter: 'search' }, { key: 'PublicModel', label: 'Model', filter: 'select' },
        { key: 'Requests', label: 'Requests' }, { key: 'InputTokens', label: 'Input Tokens' },
        { key: 'OutputTokens', label: 'Output Tokens' },
      ]} />
    </Section>}
    {tab === 4 && <Section title="Revoke Application Access">
      <Typography variant="body2" sx={{ mb: 2 }}>Suspending an approved application immediately blocks every model and revokes all associated API keys. This action does not bypass the approval queue.</Typography>
      <DataTable tableId="access" rows={applications} columns={[
        
        
        { key: 'ApplicantUser', label: 'Applicant', filter: 'search' },
        { key: 'ApplicantType', label: 'Type', filter: 'select' },
        { key: 'Status', label: 'Status', filter: 'select', render: r => <Status value={r.Status} /> },
         { key: 'ActiveGrantCount', label: 'Active Models' },
        { key: 'actions', label: 'Actions', render: r => r.Status === 'Approved' ?
          <Button color="error" disabled={busy} onClick={() => { setRevokeTarget(r); setRevokeReason(''); }}>Suspend & Revoke All</Button> : r.Status === 'Suspended' ?
          <Button color="success" disabled={busy} onClick={() => { setReinstateTarget(r); setReinstateReason(''); }}>Reinstate Access</Button> : '—' },
      ]} />
    </Section>}
    <Dialog open={Boolean(reinstateTarget)} onClose={() => !busy && setReinstateTarget(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#FFF0E3', color: '#1565C0', fontWeight: 900 }}>Reinstate AI Developer Application</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        <Typography sx={{ mb: 2 }}>Requestor: {reinstateTarget?.ApplicantUser}. Only active students/employees can be reinstated. Revoked keys stay revoked and inactive grants require reassignment.</Typography>
        <TextField fullWidth multiline minRows={3} label="Reason for reinstatement" value={reinstateReason} onChange={e => setReinstateReason(e.target.value)} />
      </DialogContent>
      <DialogActions><Button onClick={() => setReinstateTarget(null)} disabled={busy}>Cancel</Button><Button color="success" variant="contained" onClick={reinstateApplication} disabled={busy || reinstateReason.trim().length < 3}>Confirm Reinstatement</Button></DialogActions>
    </Dialog>
    <Dialog open={Boolean(revokeTarget)} onClose={() => !busy && setRevokeTarget(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: '#FFF0E3', color: '#1565C0', fontWeight: 900 }}>Suspend Application and Revoke All Keys</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        <Typography sx={{ mb: 2 }}>Project {revokeTarget?.ProjectName} — {revokeTarget?.ApplicantUser}. This will immediately disable all its API keys.</Typography>
        <TextField fullWidth multiline minRows={3} label="Reason for revocation" value={revokeReason} onChange={e => setRevokeReason(e.target.value)} />
      </DialogContent>
      <DialogActions><Button onClick={() => setRevokeTarget(null)} disabled={busy}>Cancel</Button><Button color="error" variant="contained" onClick={revokeApplication} disabled={busy || revokeReason.trim().length < 3}>Confirm Revocation</Button></DialogActions>
    </Dialog>
  </PageShell>;
}
